import React, { useState, useEffect } from 'react';
import { Table, message } from 'antd';
import { useParams } from 'react-router-dom';
import { ICommonCode, IProject } from 'utils/types/storyTypes';
import TableToolbar from 'components/table/TableToolbar';
import MainTable from 'components/table/MainTable';
import DefaultButton from 'components/button/DefaultButton';
import { theme } from 'utils/style/theme';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { handleMessage } from 'utils/helper/message';
import ActionButton from 'components/table/ActionButton';
import { Edit2, ReceiveSquare, Trash } from 'iconsax-react';
import DeleteModal from 'components/modal/DeleteModal';
import isDownload from 'hooks/isDownload';
import SearchInput from 'components/search/SearchInput';
import { characterApis } from 'apis/resource/characterApis';

import SoundUploadModal from './SoundUploadModal';
import { soundApis } from 'apis/resource/soundApis';
import { useCookies } from 'react-cookie';
import SoundEditModal from './SoundEditModal';

/**
 * * 리소스 - 배경 목록
 */

interface IProps {
  selectProject: IProject;
}

const EmoticonTable = ({ selectProject }: IProps) => {
  const { t } = useTranslation();
  const [cookie] = useCookies();
  const { projectId } = useParams(); // id: 프로젝트 id
  const [searchData, setSearchData] = useState<string>(''); // 검색어
  const [visible, setVisible] = useState({
    create: false,
    edit: false,
    delete: false,
    name: '',
  }); // 모달 show on/off
  const [selectedData, setSelectedData] = useState<any>(null);

  const [refresh, setRefresh] = useState<boolean>(false);
  const [data, setData] = useState<any>(null);
  const [charactersData, setCharacterData] = useState<ICommonCode[] | null>(
    null,
  );
  const [soundTypeData, setSoundTypeData] = useState<ICommonCode[] | null>(
    null,
  );
  const [tableData, setTableData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const handleFetchData = async () => {
    if (projectId) {
      try {
        const res = await soundApis.soundList(projectId);
        setData(res.list);
        const SoundTypeRes = await soundApis.soundTypes(cookie.lang);
        setSoundTypeData(SoundTypeRes);
        const characterRes = await characterApis.list(projectId.toString());
        setCharacterData(characterRes);
      } catch (err) {
        console.log('fetch data error: ', err);
      }
    }
  };

  useEffect(() => {
    if (data) {
      setTableData(data);
    }
  }, [data]);

  useEffect(() => {
    handleFetchData();
  }, [refresh]);

  // 배경 삭제
  const handleClickDeleteModal = (record) => {
    setSelectedData(record);
    setVisible({
      ...visible,
      delete: true,
      name: record?.sound_name,
    });
  };
  const handleDelete = async () => {
    if (selectedData) {
      try {
        setLoading(true);
        await soundApis.soundDelete(selectedData.project_id, selectedData.id);
        setRefresh(!refresh);
        setLoading(false);
        setVisible({
          ...visible,
          delete: false,
          name: selectedData?.live_name,
        });
      } catch (err) {
        setLoading(false);
        handleMessage(message, 'error', 'Please try again');
        console.log('Delete error:', err);
      }
    }
  };

  /// 다운로드
  const handleDownload = (record) => {
    // 다운로드 함수로 보내준다.
    isDownload(record);
  };

  const tableActionMenu = (record) => {
    return [
      {
        key: 1,
        item: (
          <ReceiveSquare size='24' onClick={() => handleDownload(record)} />
        ),
        hoverColor: theme.colors.green_500,
        color: theme.colors.dark_200,
      },
      {
        key: 2,
        item: (
          <Edit2
            size='24'
            onClick={() => {
              setSelectedData(record);
              setVisible({ ...visible, edit: true });
            }}
          />
        ),
        hoverColor: theme.colors.blue_500,
        color: theme.colors.dark_200,
      },

      {
        key: 3,
        item: (
          <Trash
            size='24'
            onClick={() => {
              handleClickDeleteModal(record);
            }}
          />
        ),
        hoverColor: theme.colors.red_500,
        color: theme.colors.dark_200,
      },
    ];
  };

  const columnsOrigin = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 'fit-content',
      align: 'center',
      vertical: 'middle',
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.soundContent.table.type',
      ),
      dataIndex: 'sound_type',
      width: '9rem',
      sorter: (a, b) => {
        if (a.sound_type && b.sound_type) {
          return a.sound_type.localeCompare(b.sound_type, undefined, {
            sensitivity: 'base',
          });
        }
      },
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.soundContent.table.soundName',
      ),
      dataIndex: 'sound_name',
      width: '9rem',
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.soundContent.table.play',
      ),
      dataIndex: 'sound_url',
      width: '9rem',
      render: (_) => {
        return <audio controls src={_} />;
      },
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.soundContent.table.volume',
      ),
      dataIndex: 'game_volume',
      width: '9rem',
      align: 'center',
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.soundContent.table.character',
      ),
      dataIndex: 'speaker',
      width: '9rem',
    },
    {
      title: '',
      dataIndex: '',
      width: '4rem',
      render: (text, record) => {
        const menus = [tableActionMenu(record)];
        return <ActionButton menus={menus} position='end' />;
      },
    },
    Table.EXPAND_COLUMN,
  ];

  // handle search input
  useEffect(() => {
    if (searchData && searchData.length) {
      const cloneData = JSON.parse(JSON.stringify(data));
      const filterData = cloneData.filter((e) =>
        e.sound_name.toLowerCase().includes(searchData.toLowerCase()),
      );
      setTableData(filterData);
    } else {
      setTableData(data);
    }
  }, [searchData]);

  return (
    <>
      <TableToolbar>
        <SearchInput placeholder={'Search...'} setSearchData={setSearchData} />
        <div>
          <DefaultButton
            name='register'
            onClick={() => setVisible({ ...visible, create: true })}
          >
            {t(
              'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.soundContent.header.button.create',
            )}
          </DefaultButton>
        </div>
      </TableToolbar>
      <TableWrapper>
        <MainTable rowKey='id' columns={columnsOrigin} dataSource={tableData} />
      </TableWrapper>

      <SoundUploadModal
        visible={visible}
        setVisible={setVisible}
        setRefresh={setRefresh}
        refresh={refresh}
        projectId={projectId}
        charactersData={charactersData}
        soundTypeData={soundTypeData}
      />

      <SoundEditModal
        visible={visible}
        setVisible={setVisible}
        setRefresh={setRefresh}
        refresh={refresh}
        data={selectedData}
        setData={setSelectedData}
        projectId={projectId}
        charactersData={charactersData}
        soundTypeData={soundTypeData}
      />

      <DeleteModal
        visible={visible.delete}
        setVisible={(e) => {
          setVisible({ ...visible, delete: e });
        }}
        title={t(
          'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.soundContent.deleteModal.title',
        )}
        content='storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.soundContent.deleteModal.content'
        label={visible.name}
        handleDelete={handleDelete}
        loading={loading}
      />
    </>
  );
};

export default EmoticonTable;

const TableWrapper = styled.div`
  .ant-table table tr {
    vertical-align: top;
  }
  .ant-table-thead > tr > th {
    padding: 0.8rem 0.8rem;
  }
  .ant-table-thead > tr > th,
  .ant-table-column-title {
    font-size: ${theme.fontSizes.normal};
    color: ${theme.colors.dark_300} !important;
    font-weight: ${theme.fontWeights.bold};
    line-height: 16px;
    letter-spacing: 0.006rem;
  }
  .ant-table-wrapper
    .ant-table-tbody
    > tr
    > td
    > .ant-table-wrapper:only-child
    .ant-table {
    margin: 0 10px;
  }
`;
